const EDGE_PIECE_SPACING = 2.05;
const RING_ARC_SPACING = 1.3;
const RING_BOARD_CLEARANCE = 0.6;
const RING_MIN_PIECE_CLEARANCE = 1.2;
const RING_PIECE_SPACING = 1.35;
const RING_SPACING = Math.SQRT2 * 1.08;
const RING_ROTATION = Math.PI * (3 - Math.sqrt(5));
export function edgePiecePosition(id, rows, columns) {
    const spacingX = EDGE_PIECE_SPACING / columns;
    const spacingY = EDGE_PIECE_SPACING / rows;
    const horizontalSlots = Math.max(1, Math.floor(0.9 / spacingX) + 1);
    const verticalSlots = Math.max(1, Math.floor(0.9 / spacingY) + 1);
    const edge = id % 4;
    const index = Math.floor(id / 4);
    if (edge === 0 || edge === 2) {
        const slot = index % horizontalSlots;
        const lane = Math.floor(index / horizontalSlots);
        return {
            x: 0.5 - (horizontalSlots - 1) * spacingX / 2 + slot * spacingX,
            y: edge === 0 ? -(lane + 1) * spacingY : 1 + (lane + 1) * spacingY,
        };
    }
    const slot = index % verticalSlots;
    const lane = Math.floor(index / verticalSlots);
    return {
        x: edge === 1 ? 1 + (lane + 1) * spacingX : -(lane + 1) * spacingX,
        y: 0.5 - (verticalSlots - 1) * spacingY / 2 + slot * spacingY,
    };
}
export function ringPiecePosition(id, rows, columns, pieceCount = rows * columns) {
    const rings = pieceRings(rows, columns, pieceCount);
    let ringPieceIndex = id;
    for (let ringIndex = 0; ringIndex < rings.length; ringIndex += 1) {
        const ring = rings[ringIndex];
        if (ringPieceIndex >= ring.count) {
            ringPieceIndex -= ring.count;
            continue;
        }
        const angle = -Math.PI / 2 + ringIndex * RING_ROTATION +
            ringPieceIndex / ring.count * Math.PI * 2;
        return {
            x: 0.5 + Math.cos(angle) * ring.radiusX,
            y: 0.5 + Math.sin(angle) * ring.radiusY,
        };
    }
    return { x: 0.5, y: -0.5 };
}
export function scatteredPiecePosition(id, rows, columns) {
    const random = seededUnit(id + rows * 101 + columns * 1_009);
    const edge = id % 4;
    const spread = 0.02 + random * 0.96;
    const offset = 0.13 + seededUnit(id * 17 + 31) * 0.16;
    const positions = [
        { x: spread, y: -offset },
        { x: 1 + offset, y: spread },
        { x: spread, y: 1 + offset },
        { x: -offset, y: spread },
    ];
    return positions[edge] ?? positions[0];
}
function pieceRings(rows, columns, pieceCount) {
    const spacingX = RING_PIECE_SPACING / columns;
    const spacingY = RING_PIECE_SPACING / rows;
    const baseRadiusX = Math.SQRT2 *
        (0.5 + RING_BOARD_CLEARANCE / columns);
    const baseRadiusY = Math.SQRT2 *
        (0.5 + RING_BOARD_CLEARANCE / rows);
    const rings = [];
    let totalCapacity = 0;
    for (let ringIndex = 0; totalCapacity < pieceCount; ringIndex += 1) {
        const radiusX = baseRadiusX +
            ringIndex * spacingX * RING_SPACING;
        const radiusY = baseRadiusY +
            ringIndex * spacingY * RING_SPACING;
        const capacity = ringCapacity(radiusX, radiusY, rows, columns, spacingX, spacingY);
        rings.push({ capacity, count: 0, radiusX, radiusY });
        totalCapacity += capacity;
    }
    const fractionalCounts = rings.map((ring, index) => {
        const idealCount = pieceCount * ring.capacity / totalCapacity;
        ring.count = Math.floor(idealCount);
        return { fraction: idealCount - ring.count, index };
    }).sort((first, second) => second.fraction - first.fraction);
    let unassignedPieceCount = pieceCount -
        rings.reduce((sum, ring) => sum + ring.count, 0);
    for (const { index } of fractionalCounts) {
        if (unassignedPieceCount === 0)
            break;
        rings[index].count += 1;
        unassignedPieceCount -= 1;
    }
    return rings;
}
function ringCapacity(radiusX, radiusY, rows, columns, spacingX, spacingY) {
    let capacity = Math.max(4, Math.floor(ellipseCircumference(radiusX / spacingX, radiusY / spacingY) /
        RING_ARC_SPACING));
    const minimumX = RING_MIN_PIECE_CLEARANCE / columns;
    const minimumY = RING_MIN_PIECE_CLEARANCE / rows;
    while (capacity > 4) {
        let hasClearance = true;
        for (let index = 0; index < capacity; index += 1) {
            const angle = index / capacity * Math.PI * 2;
            const nextAngle = (index + 1) / capacity * Math.PI * 2;
            const distanceX = Math.abs((Math.cos(angle) - Math.cos(nextAngle)) * radiusX);
            const distanceY = Math.abs((Math.sin(angle) - Math.sin(nextAngle)) * radiusY);
            if (distanceX < minimumX && distanceY < minimumY) {
                hasClearance = false;
                break;
            }
        }
        if (hasClearance)
            return capacity;
        capacity -= 1;
    }
    return capacity;
}
function ellipseCircumference(radiusX, radiusY) {
    return Math.PI * (3 * (radiusX + radiusY) -
        Math.sqrt((3 * radiusX + radiusY) * (radiusX + 3 * radiusY)));
}
function seededUnit(seed) {
    let value = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b);
    value ^= value >>> 13;
    value = Math.imul(value, 0xc2b2ae35);
    value ^= value >>> 16;
    return (value >>> 0) / 4_294_967_295;
}
//# sourceMappingURL=piece-spawn.js.map